import { useEffect, useState, useCallback } from 'react';

interface Props {
  elapsedMs: number;
  totalDurationMin: number;
  onHalfway?: () => void;
  onFiveMinWarning?: () => void;
  halfwayEnabled?: boolean;
  fiveMinEnabled?: boolean;
}

function formatTime(seconds: number): string {
  const absSeconds = Math.abs(seconds);
  const min = Math.floor(absSeconds / 60);
  const sec = absSeconds % 60;
  const sign = seconds < 0 ? '-' : '';
  return `${sign}${min}:${sec.toString().padStart(2, '0')}`;
}

export function TimeHealthBar({
  elapsedMs,
  totalDurationMin,
  onHalfway,
  onFiveMinWarning,
  halfwayEnabled = true,
  fiveMinEnabled = true,
}: Props) {
  const [halfwayAlertShown, setHalfwayAlertShown] = useState(false);
  const [fiveMinAlertShown, setFiveMinAlertShown] = useState(false);

  const totalSeconds = totalDurationMin * 60;
  const elapsedSeconds = Math.floor(elapsedMs / 1000);
  const remainingSeconds = totalSeconds - elapsedSeconds;
  const pctElapsed = Math.min(100, (elapsedSeconds / totalSeconds) * 100);
  const isOvertime = remainingSeconds < 0;

  // Alert triggers
  useEffect(() => {
    // Halfway alert (at 50%)
    if (halfwayEnabled && pctElapsed >= 50 && !halfwayAlertShown) {
      setHalfwayAlertShown(true);
      onHalfway?.();
    }

    // 5 minute warning
    if (fiveMinEnabled && remainingSeconds <= 300 && remainingSeconds > 0 && !fiveMinAlertShown) {
      setFiveMinAlertShown(true);
      onFiveMinWarning?.();
    }
  }, [pctElapsed, remainingSeconds, halfwayAlertShown, fiveMinAlertShown, halfwayEnabled, fiveMinEnabled, onHalfway, onFiveMinWarning]);

  // Determine bar color based on remaining time
  const getBarColor = useCallback(() => {
    if (isOvertime) return 'bg-red-500';
    const pctRemaining = 100 - pctElapsed;
    if (pctRemaining < 17) return 'bg-red-500';     // < ~5 min for 30 min meeting
    if (pctRemaining < 33) return 'bg-yellow-500';  // < ~10 min
    return 'bg-green-500';
  }, [isOvertime, pctElapsed]);

  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-center text-xs">
        <span className="font-mono text-gray-600">{formatTime(elapsedSeconds)}</span>
        <span className={`font-mono ${isOvertime ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
          {isOvertime ? 'OVERTIME +' + formatTime(Math.abs(remainingSeconds)) : formatTime(remainingSeconds) + ' left'}
        </span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-500 ${getBarColor()}`}
          style={{ width: `${Math.min(100, pctElapsed)}%` }}
        />
      </div>
      <div className="flex justify-between text-xs text-gray-400">
        <span>{totalDurationMin} min meeting</span>
        <span>{Math.round(100 - pctElapsed)}% remaining</span>
      </div>
    </div>
  );
}
